import { useEffect, useState } from "react";
import { api } from "../api";
import { TrendingUp, MapPin, Building2, BarChart3 } from "lucide-react";
import SEO from "../components/SEO";

export default function Osservatorio() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/platform/osservatorio").then(r => setData(r.data)); }, []);
  if (!data) return <main className="py-32 container-x"><div className="font-mono text-xs" style={{ opacity: 0.6 }}>Loading...</div></main>;
  const trendColor = { "in crescita": "#065F46", "stabile": "var(--midnight)", "in contrazione": "var(--terracotta)", "premium": "#7c3aed" };
  return (
    <main data-testid="osservatorio-page">
      <SEO
        title="Osservatorio successioni PMI"
        description="L'indice trimestrale del passaggio generazionale in Italia. Multipli EBITDA reali per settore, benchmark regionali, tempi medi di closing del micro-mid market italiano. Q3 2026."
        path="/osservatorio"
      />
      <section style={{ background: "var(--midnight)", color: "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--gold)" }}>Osservatorio · {data.quarter}</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 1100 }}>
            L'indice trimestrale<br />
            <span style={{ color: "var(--gold)", fontStyle: "italic" }}>del passaggio generazionale.</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 780, opacity: 0.85, lineHeight: 1.7 }}>
            I multipli reali del micro-market italiano che oggi non esistono. Aggregati da negoziazioni concrete, non da report generici. Riservato ai clienti Istituzionale.
          </p>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Segnali di mercato</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Il mercato in tre numeri</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-10 pt-8" style={{ borderTop: "1px solid var(--border)" }}>
            <Metric value={(data.market_signals.titolari_over_65_stimati / 1000).toFixed(0) + "k"} label="Titolari PMI oltre i 65 anni" unit="stimati" />
            <Metric value={(data.market_signals.pmi_in_transizione_5y / 1000).toFixed(0) + "k"} label="PMI in transizione entro 5 anni" unit="stima 2026-2031" />
            <Metric value={data.market_signals.deal_chiusi_micro_market_annuo.toLocaleString('it-IT')} label="Deal chiusi/anno micro-mid market" unit="benchmark" />
            <Metric value={data.market_signals.tempo_medio_deal_giorni + " gg"} label="Tempo medio deal (mercato)" unit="da mandato a closing" />
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--muted)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>GIANO Deal Machine</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Il nostro benchmark</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-10 pt-8" style={{ borderTop: "1px solid var(--border)" }}>
            <Metric value={data.giano_pipeline.valuations_generated} label='Valutazioni "Quanto Vale" generate' unit="cumulativo" />
            <Metric value={data.giano_pipeline.avg_time_to_offer_days + " gg"} label="Tempo medio offerta GIANO" unit="vs 60-90 di mercato" />
            <Metric value={data.giano_pipeline.avg_time_to_close_days + " gg"} label="Tempo medio closing GIANO" unit="vs 380 di mercato" />
            <Metric value={data.giano_pipeline.acceleration_vs_market_pct + "%"} label="Accelerazione vs mercato" unit="tempo risparmiato" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ background: "var(--card)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Multipli EBITDA</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Range indicativi per settore</h2>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--midnight)" }}>
                  <th className="text-left font-mono text-xs py-3">SETTORE</th>
                  <th className="text-right font-mono text-xs py-3">MIN</th>
                  <th className="text-right font-mono text-xs py-3">MAX</th>
                  <th className="text-right font-mono text-xs py-3">SPREAD</th>
                  <th className="text-right font-mono text-xs py-3">TREND</th>
                </tr>
              </thead>
              <tbody>
                {data.sector_multiples_indicativi.map(s => (
                  <tr key={s.sector} style={{ borderBottom: "1px solid var(--border)" }} data-testid={`sector-row-${s.sector}`}>
                    <td className="py-4 serif-display" style={{ fontSize: 20 }}>{s.sector}</td>
                    <td className="text-right font-mono">{s.ev_ebitda_low}x</td>
                    <td className="text-right font-mono">{s.ev_ebitda_high}x</td>
                    <td className="text-right font-mono" style={{ opacity: 0.6 }}>+{(s.ev_ebitda_high - s.ev_ebitda_low).toFixed(1)}x</td>
                    <td className="text-right"><span className="chip" style={{ background: trendColor[s.trend], color: "white", borderColor: "transparent", fontSize: 11 }}>{s.trend}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: "var(--bone)" }}>
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>Mappa territoriale interattiva</div>
          <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Le regioni del deal</h2>
          <p className="mt-3 text-sm" style={{ opacity: 0.7, maxWidth: 700 }}>
            Passa il mouse su ogni regione per vedere quota di mercato, multiplo EBITDA medio, PMI in transizione e settore principale. Dati Q3 2026.
          </p>
          <RegionMap regions={data.regions_all || data.regions_top} />
        </div>
      </section>
    </main>
  );
}

// --- Mappa regioni interattiva (cartogramma stilizzato) ---
// Posizioni approssimate delle 20 regioni italiane (griglia semplificata a hexbin).
const REGION_LAYOUT = {
  "Valle d'Aosta":       { row: 0, col: 1 },
  "Piemonte":            { row: 1, col: 1 },
  "Lombardia":           { row: 1, col: 2 },
  "Trentino-Alto Adige": { row: 0, col: 3 },
  "Veneto":              { row: 1, col: 3 },
  "Friuli-Venezia Giulia":{row: 1, col: 4 },
  "Liguria":             { row: 2, col: 1 },
  "Emilia-Romagna":      { row: 2, col: 2 },
  "Toscana":             { row: 3, col: 2 },
  "Marche":              { row: 3, col: 3 },
  "Umbria":              { row: 4, col: 2 },
  "Lazio":               { row: 4, col: 3 },
  "Abruzzo":             { row: 4, col: 4 },
  "Molise":              { row: 5, col: 4 },
  "Campania":            { row: 5, col: 3 },
  "Puglia":              { row: 5, col: 5 },
  "Basilicata":          { row: 6, col: 4 },
  "Calabria":            { row: 7, col: 4 },
  "Sardegna":            { row: 5, col: 1 },
  "Sicilia":             { row: 8, col: 3 },
};

function RegionMap({ regions }) {
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const maxShare = Math.max(...regions.map(r => r.share_pct || 0));

  const heat = (share) => {
    const t = share / maxShare;
    // Terracotta gradient: light to strong
    const r = Math.round(247 - t * 74);   // 247 → 173
    const g = Math.round(245 - t * 169);  // 245 → 76
    const b = Math.round(240 - t * 181);  // 240 → 59
    return `rgb(${r},${g},${b})`;
  };

  const cell = 78;
  const gap = 5;
  const cols = 7;
  const rows = 9;
  const width = cols * (cell + gap);
  const height = rows * (cell + gap);
  const detail = selected || hovered;

  return (
    <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-8">
      <div className="p-6" style={{ background: "var(--card)", border: "1px solid var(--border)" }} data-testid="region-map">
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ maxWidth: 620, display: "block", margin: "0 auto" }}>
          {regions.map(r => {
            const layout = REGION_LAYOUT[r.region];
            if (!layout) return null;
            const x = layout.col * (cell + gap);
            const y = layout.row * (cell + gap);
            const isActive = detail?.region === r.region;
            return (
              <g
                key={r.region}
                onMouseEnter={() => setHovered(r)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => setSelected(selected?.region === r.region ? null : r)}
                data-testid={`region-cell-${r.region}`}
                style={{ cursor: "pointer" }}
              >
                <rect
                  x={x} y={y}
                  width={cell} height={cell}
                  fill={heat(r.share_pct || 0)}
                  stroke={isActive ? "var(--midnight)" : "var(--border)"}
                  strokeWidth={isActive ? 3 : 1}
                />
                <text
                  x={x + cell / 2}
                  y={y + cell / 2 + 5}
                  textAnchor="middle"
                  fontFamily="serif"
                  fontSize="13"
                  fontWeight="600"
                  fill={r.share_pct >= maxShare * 0.5 ? "var(--bone)" : "var(--midnight)"}
                >
                  {r.region.length > 9 ? r.region.substring(0, 8) + "…" : r.region}
                </text>
              </g>
            );
          })}
        </svg>
        <div className="mt-5 flex items-center justify-center gap-4 text-xs font-mono" style={{ opacity: 0.6 }}>
          <span>Bassa quota</span>
          <div style={{ display: "flex" }}>
            {[0, 0.25, 0.5, 0.75, 1].map(t => (
              <div key={t} style={{ width: 30, height: 12, background: heat(t * maxShare) }} />
            ))}
          </div>
          <span>Alta quota</span>
        </div>
      </div>

      {/* Detail panel */}
      <div className="p-6" style={{ background: "var(--midnight)", color: "var(--bone)", minHeight: 340 }} data-testid="region-detail-panel">
        {detail ? (
          <>
            <div className="overline" style={{ color: "var(--gold)" }}>{detail.code || "Regione"}</div>
            <h3 className="serif-display mt-3" style={{ fontSize: 32 }}>{detail.region}</h3>
            <div className="mt-6 space-y-4">
              <DetailRow icon={<TrendingUp size={16} style={{ color: "var(--gold)" }} />} k="Quota di mercato" v={`${(detail.share_pct || 0).toFixed(1)}%`} />
              <DetailRow icon={<BarChart3 size={16} style={{ color: "var(--gold)" }} />} k="Multiplo EBITDA medio" v={`${detail.avg_multiple || "—"}x`} />
              {detail.pmi_transizione && <DetailRow icon={<Building2 size={16} style={{ color: "var(--gold)" }} />} k="PMI in transizione" v={detail.pmi_transizione.toLocaleString("it-IT")} />}
              {detail.top_sector && <DetailRow icon={<MapPin size={16} style={{ color: "var(--gold)" }} />} k="Settore principale" v={detail.top_sector} />}
            </div>
            <div className="mt-8 pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
              <a href="/marketplace" className="text-sm font-mono" style={{ color: "var(--gold)", textDecoration: "underline", textUnderlineOffset: 4 }}>
                Vedi dossier attivi in {detail.region} →
              </a>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center" style={{ opacity: 0.7 }}>
            <MapPin size={32} style={{ color: "var(--gold)" }} />
            <p className="mt-4 text-sm" style={{ maxWidth: 240, lineHeight: 1.6 }}>
              Passa il mouse o clicca su una regione per esplorare i dati del passaggio generazionale.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ icon, k, v }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">{icon}<span className="text-sm" style={{ opacity: 0.75 }}>{k}</span></div>
      <span className="serif-display" style={{ fontSize: 22 }}>{v}</span>
    </div>
  );
}

function Metric({ value, label, unit }) {
  return (
    <div data-testid={`metric-${label}`}>
      <div className="font-mono text-xs" style={{ opacity: 0.5 }}>{unit}</div>
      <div className="serif-display mt-2" style={{ fontSize: 48, lineHeight: 1 }}>{value}</div>
      <div className="mt-3 text-sm" style={{ opacity: 0.75 }}>{label}</div>
    </div>
  );
}
